<template>
    <div v-if="isInitialized" class="chatbot-initializer" />
</template>

<script>
import { createNamespacedHelpers } from 'vuex';

import { initialState } from '@/Helpers.js';

const { mapState } = createNamespacedHelpers('Auth');

export default {
    name: 'ChatbotInitializer',

    data: () => ({
        isInitialized: false,
        scriptLoaded: false,
    }),

    computed: {
        ...mapState(['user']),
        chatbotConfig() {
            return {
                enabled: initialState('ChatbotConfig.enabled', true),
                widgetId: initialState('ChatbotConfig.widgetId', ''),
                scriptUrl: initialState('ChatbotConfig.scriptUrl', ''),
                roleMapping: initialState('ChatbotConfig.roleMapping', {}),
                primaryGroupCategories: initialState('ChatbotConfig.primaryGroupCategories', {}),
                widgetConfig: initialState('ChatbotConfig.widgetConfig', {}),
            };
        },
        userRole() {
            if (!this.user) return null;
            const userRoles = this.user.roles || [];
            const mappedRole = userRoles
                .map(role => this.chatbotConfig.roleMapping[role.name])
                .find(mapping => mapping);
            return mappedRole || 'edverum_student';
        },
        primaryGroup() {
            if (!this.user || !this.user.userGroups) return null;
            const institutionName = initialState('InstitutionSettings.name', '').toLowerCase();
            const categoryName =
                this.chatbotConfig.primaryGroupCategories[institutionName] ||
                this.chatbotConfig.primaryGroupCategories.default ||
                'Cohort';
            const primaryGroup = this.user.userGroups.find(group => group.group?.category?.name === categoryName);
            return primaryGroup?.group?.name || null;
        },
        shouldInitialize() {
            return (
                this.user &&
                this.chatbotConfig.enabled &&
                !this.isInitialized &&
                !this.$store.getters['User/hasActiveAttempt'] &&
                this.isHelpPage // Only initialize on help page
            );
        },
        isHelpPage() {
            return this.$route.path === '/help';
        },
    },

    watch: {
        user: {
            handler() {
                if (this.shouldInitialize) this.initializeChatbot();
            },
            immediate: true,
        },
        '$store.getters.User/hasActiveAttempt': {
            handler(hasActiveAttempt) {
                if (hasActiveAttempt && this.isInitialized) {
                    this.destroyChatbot();
                } else if (!hasActiveAttempt && this.shouldInitialize) {
                    this.initializeChatbot();
                }
            },
            immediate: true,
        },
        $route: {
            handler() {
                if (this.isHelpPage) {
                    // On help page - initialize if not already done
                    if (this.shouldInitialize) {
                        this.initializeChatbot();
                    } else if (this.isInitialized) {
                        // Already initialized, just show it
                        this.showChatbot();
                    }
                } else if (this.isInitialized) {
                    // Not on help page - destroy if initialized
                    this.destroyChatbot();
                }
            },
            immediate: true,
        },
    },

    beforeUnmount() {
        this.destroyChatbot();
    },

    methods: {
        async initializeChatbot() {
            if (!this.shouldInitialize) return;
            if (!this.chatbotConfig.widgetId || !this.chatbotConfig.scriptUrl) {
                console.warn('Chatbot: Widget ID or Script URL missing.');
                return;
            }

            // Set up Freshchat config
            window.fcWidgetMessengerConfig = {
                config: {
                    eagerLoad: this.chatbotConfig.widgetConfig.eager_load ?? true,
                    fullscreen: this.chatbotConfig.widgetConfig.fullscreen ?? false,
                    headerProperty: {
                        appName: this.chatbotConfig.widgetConfig.app_name ?? 'Edverum Support',
                    },
                },
                tags: [this.userRole],
                externalId: this.user.id.toString(),
            };
            window.fcSettings = {
                onInit: () => {
                    if (!window.fcWidget) return;
                    window.fcWidget.setExternalId(this.user.id.toString());
                    window.fcWidget.user.setFirstName(this.user.first_name);
                    window.fcWidget.user.setLastName(this.user.last_name);
                    window.fcWidget.user.setEmail(this.user.email);
                    window.fcWidget.user.setProperties({
                        cf_external_id: this.user.external_id || '',
                        cf_primary_group: this.primaryGroup || '',
                        cf_user_role: this.userRole,
                        cf_institution: initialState('InstitutionSettings.name', ''),
                        cf_user_id: this.user.id.toString(),
                    });
                },
            };
            // Load script if not already loaded
            if (!this.scriptLoaded && !document.querySelector(`script[src="${this.chatbotConfig.scriptUrl}"]`)) {
                await this.loadFreshchatScript();
            }
            this.isInitialized = true;
            // Enhanced debug API with visibility control
            window.edverumChatbot = {
                open: () => window.fcWidget && window.fcWidget.open(),
                close: () => window.fcWidget && window.fcWidget.close(),
                isOpen: () => window.fcWidget && window.fcWidget.isOpen(),
                show: () => this.showChatbot(),
                hide: () => this.hideChatbot(),
                toggle: () => this.toggleChatbot(),
            };
        },

        showChatbot() {
            if (!window.fcWidget) return;

            // Add chatbot-visible class to body
            document.body.classList.add('chatbot-visible');

            // Also ensure elements are visible with inline styles as backup
            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.display = 'block';
                fcFrame.style.visibility = 'visible';
                fcFrame.style.opacity = '1';
            }

            // Also show any launcher elements
            const launcherElements = document.querySelectorAll(
                '[data-testid="launcher"], .fc-launcher, [class*="launcher"]'
            );
            launcherElements.forEach(element => {
                element.style.display = 'block';
                element.style.visibility = 'visible';
                element.style.opacity = '1';
            });
        },

        hideChatbot() {
            if (!window.fcWidget) return;

            // Remove chatbot-visible class from body
            document.body.classList.remove('chatbot-visible');

            // Hide the fc_frame element
            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame) {
                fcFrame.style.display = 'none';
                fcFrame.style.visibility = 'hidden';
                fcFrame.style.opacity = '0';
            }

            // Also hide any launcher elements
            const launcherElements = document.querySelectorAll(
                '[data-testid="launcher"], .fc-launcher, [class*="launcher"]'
            );
            launcherElements.forEach(element => {
                element.style.display = 'none';
                element.style.visibility = 'hidden';
                element.style.opacity = '0';
            });
        },

        toggleChatbot() {
            if (!window.fcWidget) return;

            const fcFrame = document.getElementById('fc_frame');
            if (fcFrame && fcFrame.style.display !== 'none') {
                this.hideChatbot();
            } else {
                this.showChatbot();
            }
        },

        loadFreshchatScript() {
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                // Add version parameter to force cache busting
                const scriptUrl = this.chatbotConfig.scriptUrl.includes('?')
                    ? `${this.chatbotConfig.scriptUrl}&v=${Date.now()}`
                    : `${this.chatbotConfig.scriptUrl}?v=${Date.now()}`;
                script.src = scriptUrl;
                script.async = true;
                script.setAttribute('widgetId', this.chatbotConfig.widgetId);
                script.onload = () => {
                    this.scriptLoaded = true;
                    resolve();
                };
                script.onerror = () => {
                    console.error('Chatbot: Failed to load Freshchat script.');
                    reject();
                };
                document.head.appendChild(script);
            });
        },

        destroyChatbot() {
            if (window.fcWidget && typeof window.fcWidget.destroy === 'function') {
                window.fcWidget.destroy();
            }
            this.isInitialized = false;
            this.scriptLoaded = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.chatbot-initializer {
}
</style>
